import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, User } from 'lucide-react';
import { bookingPublicApi, DomainBookingType } from '../../services/api';
import { brandingVars, brandingLogo, BookingBranding } from './bookingBranding';
import './Booking.css';

type PageState = 'loading' | 'error' | 'list';

/**
 * Public, unauthenticated list of the current custom domain's active booking types.
 * Reached at /bookings on a customer's connected custom domain (see isPublicCustomDomainHost /
 * App.tsx PUBLIC_HOST guard). Reuses the same .bk-page / .bk-card shell and --bk-* branding
 * mapping as PublicBooking.tsx — never re-derive that mapping here (see bookingBranding.ts).
 */
const PublicBookingList: React.FC = () => {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [types, setTypes] = useState<DomainBookingType[]>([]);
  const [branding, setBranding] = useState<BookingBranding | null>(null);

  const bkStyle = useMemo(() => brandingVars(branding), [branding]);
  const logoUrl = brandingLogo(branding);

  const load = useCallback(async () => {
    try {
      const res = await bookingPublicApi.getDomainTypes();
      setBranding(res.branding ?? null);
      setTypes(res.types ?? []);
      setPageState('list');
    } catch {
      // Never echo the requesting host back into the DOM on an unauthenticated page.
      setPageState('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ─ Loading ─
  if (pageState === 'loading') {
    return (
      <div className="bk-page bk-scope">
        <div className="bk-card">
          <p className="bk-desc" style={{ textAlign: 'center' }}>Loading…</p>
        </div>
      </div>
    );
  }

  // ─ Error ─
  if (pageState === 'error') {
    return (
      <div className="bk-page bk-scope">
        <div className="bk-card">
          <h1 className="bk-title">Unavailable</h1>
          <p className="bk-desc">Bookings aren&apos;t available at this address.</p>
        </div>
      </div>
    );
  }

  // ─ List ─
  return (
    <div className="bk-page bk-scope" style={bkStyle}>
      <div className="bk-card">
        {logoUrl && (
          <img
            className="bk-logo"
            src={logoUrl}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <h1 className="bk-title">Book an appointment</h1>

        {types.length === 0 ? (
          <div className="bk-empty">
            <p>No bookings available</p>
            <p>There are no bookable appointment types on this site right now.</p>
          </div>
        ) : (
          <div className="bk-type-list">
            {types.map((t) => (
              <div key={t.booking_type_id} className="bk-type-row">
                <div className="bk-type-row-info">
                  <div className="bk-type-row-name">{t.name}</div>
                  {t.description && <div className="bk-type-row-desc">{t.description}</div>}
                  <div className="bk-meta">
                    <span className="bk-meta-item">
                      <Clock size={14} />
                      {t.duration_minutes} min
                    </span>
                    {t.location && (
                      <span className="bk-meta-item">
                        <MapPin size={14} />
                        {t.location}
                      </span>
                    )}
                    {t.resource_name && (
                      <span className="bk-meta-item">
                        <User size={14} />
                        {t.resource_name}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={`/bookings/${t.booking_type_id}`}
                  className="bk-slot-btn bk-type-row-cta"
                >
                  Book
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBookingList;
